import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Tooltip, message, Dropdown, Modal } from 'antd';
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  ArrowRightOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  ThunderboltOutlined,
  ClockCircleOutlined,
  SendOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  CalendarOutlined,
  MoreOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { Search, SearchStatus } from '../../../types';
import { updateSearchStatus, deleteSearch } from '../../../services/api';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import { captureEvent } from '../../../services/analytics';
import PipelineChip from './PipelineChip';
import TailoredForThisRoleBadge from '../../../components/tailor/TailoredForThisRoleBadge';
import ResumePreviewLink from '../../../components/tailor/ResumePreviewLink';
import css from './SearchPanel.module.scss';

interface SearchPanelProps {
  search: Search;
  onUpdate: () => void;
  onOpenProgress?: (search: Search) => void;
}

const statusConfig: Record<SearchStatus, { color: string; bgColor: string; label: string; glowColor: string }> = {
  active: { color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.1)', label: 'Active', glowColor: 'rgba(16, 185, 129, 0.4)' },
  pending: { color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.1)', label: 'Pending', glowColor: 'rgba(245, 158, 11, 0.4)' },
  paused: { color: '#6b7280', bgColor: 'rgba(107, 114, 128, 0.1)', label: 'Paused', glowColor: 'rgba(107, 114, 128, 0.3)' },
};

const SearchPanel: React.FC<SearchPanelProps> = ({ search, onUpdate, onOpenProgress }) => {
  const navigate = useNavigate();
  const { id: routeSearchId } = useParams<{ id?: string }>();
  const [toggling, setToggling] = useState(false);
  const config = statusConfig[search.status];
  const isPending = search.status === 'pending';
  const isActive = search.status === 'active';
  // A search's tailored resume counts only once its auto-tailor is READY. Both
  // the emerald badge (rendered below) and the preview link's eyebrow derive
  // from this single value, so the two can never disagree about "tailored".
  const readyTailoredVersionId =
    search.autoTailorStatus === 'ready' ? search.tailoredVersionId : null;
  const isTailored = !!readyTailoredVersionId;

  const handleStatClick = (tab?: 'rejected' | 'filtered') => {
    if (isPending) return;
    const params = new URLSearchParams({ searchId: search.id });
    const basePath = tab ? `/opportunities/${tab}` : '/opportunities';
    navigate(`${basePath}?${params.toString()}`);
  };

  const handleViewOpportunities = () => {
    navigate(`/opportunities?searchId=${search.id}`);
  };

  const handleTogglePause = async () => {
    const newStatus = isActive ? 'paused' : 'active';
    setToggling(true);
    try {
      await updateSearchStatus(search.id, newStatus);
      captureEvent('search_status_changed', {
        status: newStatus,
        previous_status: search.status,
      });
      message.success(`Search ${newStatus === 'paused' ? 'paused' : 'resumed'} successfully`);
      onUpdate();
    } catch (error) {
      message.error(getApiErrorMessage(error));
    } finally {
      setToggling(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Delete this search?',
      icon: <ExclamationCircleOutlined />,
      content:
        "This permanently removes the search and everything under it — its opportunities, " +
        "the applications you've sent, and their history. This can't be undone.",
      okText: 'Delete search',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteSearch(search.id);
          captureEvent('search_deleted', { previous_status: search.status });
          message.success('Search deleted');
          // No per-search detail page exists; /searches/:id only highlights a card.
          // If the deleted card is the deep-linked one, clear the param first so the
          // highlight effect early-returns instead of firing a "Search not found" toast.
          if (routeSearchId === search.id) {
            navigate('/searches', { replace: true });
          }
          onUpdate();
        } catch (error) {
          // Match the app's Modal.confirm pattern (see OpportunityDrawer): surface the
          // failure via a toast and let the modal close. Real errors are already
          // reported to Sentry by the api interceptor — re-throwing here would
          // double-report as an unhandled rejection. The user can retry from the kebab.
          message.error(getApiErrorMessage(error));
        }
      },
    });
  };

  const stats = [
    {
      key: 'opportunities',
      value: search.stats.totalOpportunities,
      label: 'Opportunities',
      icon: <ThunderboltOutlined />,
      color: '#207551',
      onClick: () => handleStatClick()
    },
    {
      key: 'pending',
      value: search.stats.pendingApproval,
      label: 'Pending',
      icon: <ClockCircleOutlined />,
      color: '#f59e0b',
      onClick: () => handleStatClick()
    },
    {
      key: 'applied',
      value: search.stats.applied,
      label: 'Applied',
      icon: <SendOutlined />,
      color: '#3b82f6',
      onClick: () => navigate(`/applications?searchId=${search.id}`)
    },
    {
      key: 'rejected',
      value: search.stats.rejected,
      label: 'Rejected',
      icon: <CloseCircleOutlined />,
      color: '#ef4444',
      onClick: () => handleStatClick('rejected')
    },
  ];

  return (
    <div className={css.searchPanel} data-search-id={search.id}>
      {/* Accent bar */}
      <div
        className={`${css.accentBar} ${isPending ? css.shimmer : ''}`}
        style={{ background: config.color }}
      />

      <div className={css.content}>
        {/* Header */}
        <div className={css.header}>
          <div className={css.titleSection}>
            <div className={css.iconWrapper}>
              <SearchOutlined />
            </div>
            <div className={css.titleContent}>
              <h3 className={css.role}>{search.role}</h3>
              <div className={css.meta}>
                <span className={css.metaItem}>
                  <EnvironmentOutlined />
                  {search.location}
                </span>
                {search.isRemote && (
                  <>
                    <span className={css.metaSeparator}>·</span>
                    <span className={css.metaItem}>
                      <GlobalOutlined /> Remote{search.residence?.trim() ? ` · from ${search.residence.trim()}` : ''}
                    </span>
                  </>
                )}
                <span className={css.metaSeparator}>·</span>
                <span className={css.metaItem}>
                  <CalendarOutlined />
                  {new Date(search.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
            </div>
          </div>

          <div className={css.statusSection}>
            {/* Chip renders only once a parent supplies an open handler (the /searches page does). */}
            {onOpenProgress && <PipelineChip search={search} onOpen={() => onOpenProgress(search)} />}
            <div className={css.statusActions}>
              {/* Universal per-card resume preview — the resume actually bound
                  to this search (tailored if tailored, else base). Gated on
                  resumeAvailable, NOT resumeVersionId: a base upload has a resume
                  but no version row, and it must still be previewable. Falls back
                  to the version id during a backend/frontend deploy skew. Additive
                  to, and independent of, the emerald tailored badge below. */}
              {(search.resumeAvailable ?? !!search.resumeVersionId) && (
                <ResumePreviewLink
                  searchId={search.id}
                  resumeVersionId={search.resumeVersionId ?? null}
                  role={search.role}
                  tailored={isTailored}
                />
              )}
              <div
                className={`${css.statusBadge} ${isActive ? css.active : ''}`}
                style={{
                  color: config.color,
                  backgroundColor: config.bgColor,
                  '--glow-color': config.glowColor
                } as React.CSSProperties}
              >
                <span className={css.statusDot} style={{ backgroundColor: config.color }} />
                {config.label}
              </div>
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [{
                    key: 'delete',
                    danger: true,
                    icon: <DeleteOutlined />,
                    label: 'Delete search',
                    onClick: handleDelete,
                  }],
                }}
              >
                <button
                  type="button"
                  className={css.kebabBtn}
                  aria-label={`Options for ${search.role} in ${search.location}`}
                >
                  <MoreOutlined />
                </button>
              </Dropdown>
            </div>
          </div>
        </div>

        {/* Tailored-resume badge — only once a ready version is bound to this search */}
        {readyTailoredVersionId && (
          <div className={css.tailorBadgeSection}>
            <TailoredForThisRoleBadge
              tailoredVersionId={readyTailoredVersionId}
              role={search.role}
              searchId={search.id}
              onChanged={onUpdate}
              versionOrdinal={search.resumeVersionOrdinal}
            />
          </div>
        )}

        {/* Stats Grid */}
        <div className={css.statsGrid}>
          {stats.map((stat) => (
            <Tooltip
              key={stat.key}
              title={isPending ? 'Search is pending' : `View ${stat.label.toLowerCase()}`}
            >
              <div
                className={`${css.statCard} ${isPending ? css.disabled : ''}`}
                onClick={isPending ? undefined : stat.onClick}
              >
                <div className={css.statIcon} style={{ color: stat.color, backgroundColor: `${stat.color}15` }}>
                  {stat.icon}
                </div>
                <div className={css.statInfo}>
                  <span className={css.statValue}>
                    {isPending ? '—' : stat.value.toLocaleString()}
                  </span>
                  <span className={css.statLabel}>{stat.label}</span>
                </div>
              </div>
            </Tooltip>
          ))}
        </div>

        {/* Actions */}
        <div className={css.actions}>
          {search.status === 'paused' ? (
            <Button
              className={css.secondaryBtn}
              icon={<PlayCircleOutlined />}
              onClick={handleTogglePause}
              loading={toggling}
            >
              Resume Search
            </Button>
          ) : search.status === 'active' ? (
            <Button
              className={css.secondaryBtn}
              icon={<PauseCircleOutlined />}
              onClick={handleTogglePause}
              loading={toggling}
            >
              Pause Search
            </Button>
          ) : null}

          <Button
            type="primary"
            className={css.primaryBtn}
            icon={<ArrowRightOutlined />}
            onClick={handleViewOpportunities}
            disabled={isPending}
          >
            View Opportunities
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SearchPanel;
